'use client'

import { motion } from 'framer-motion'
import { FaChartLine, FaSitemap, FaShieldAlt, FaHandshake, FaBolt, FaRegCompass } from 'react-icons/fa'

const method = [
    { label: 'Audit', icon: <FaRegCompass aria-hidden='true' className='h-4 w-4' /> },
    { label: 'Trigger map', icon: <FaSitemap aria-hidden='true' className='h-4 w-4' /> },
    { label: 'Regulate', icon: <FaShieldAlt aria-hidden='true' className='h-4 w-4' /> },
    { label: 'Strength align', icon: <FaHandshake aria-hidden='true' className='h-4 w-4' /> },
    { label: 'Architecture', icon: <FaBolt aria-hidden='true' className='h-4 w-4' /> },
    { label: '30-day plan', icon: <FaChartLine aria-hidden='true' className='h-4 w-4' /> }
]

const performance = [
    { title: 'Execution consistency', desc: 'Goals land more reliably.' },
    { title: 'Feedback lift', desc: 'Visible 360° improvements.' },
    { title: 'Engagement', desc: 'Less friction, more ownership.' },
    { title: 'Collaboration', desc: 'Cleaner cross-functional delivery.' }
]

const strategic = [
    { title: 'Sustainability', desc: 'Reduced burnout risk.' },
    { title: 'Retention', desc: 'Support that keeps talent.' },
    { title: 'Leadership pipeline', desc: 'Accountable performance culture.' },
    { title: 'DEI advancement', desc: 'Strength alignment at scale.' }
]

function Tile({ title, desc }: { title: string; desc: string }) {
    return (
        <div className='rounded-lg bg-white p-4 shadow-sm'>
            <p className='text-sm font-semibold'>{title}</p>
            <p className='mt-2 text-sm text-muted'>{desc}</p>
        </div>
    )
}

export default function OutcomesSection() {
    return (
        <section className='bg-page text-text'>
            <div className='container-x section-pad'>
                <div className='max-w-205'>
                    <p className='text-[11px] font-semibold tracking-[0.28em] text-muted'>OUTCOMES</p>
                    <h2 className='mt-3 text-balance text-4xl font-semibold tracking-tight sm:text-5xl'>
                        Less stress noise.
                        <span className='block'>More strength-led execution.</span>
                    </h2>
                    <p className='mt-4 text-pretty text-muted sm:text-lg'>
                        A repeatable method that leaders and teams can adopt—without bureaucracy.
                    </p>
                </div>

                {/* Compact method strip */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.45 }}
                    className='mt-10 surface p-5'>
                    <div className='flex items-center justify-between gap-6'>
                        <p className='text-sm font-semibold'>Method</p>
                        <p className='text-sm text-muted'>A clear sequence</p>
                    </div>

                    <div className='mt-4 flex flex-wrap items-center gap-2'>
                        {method.map((s, i) => (
                            <span key={s.label} className='inline-flex items-center gap-2'>
                                <span className='inline-flex items-center gap-2 rounded-full bg-[color-mix(in_oklab,var(--color-soft)_92%,var(--color-accent)_8%)] px-3 py-2 text-xs font-semibold text-text shadow-sm'>
                                    {s.icon}
                                    {s.label}
                                </span>
                                {i < method.length - 1 ? (
                                    <span
                                        className='h-1.5 w-6 rounded-full bg-[color-mix(in_oklab,var(--color-soft)_88%,var(--color-accent-2)_12%)]'
                                        aria-hidden='true'
                                    />
                                ) : null}
                            </span>
                        ))}
                    </div>
                </motion.div>

                {/* Two compact columns */}
                <div className='mt-8 grid gap-6 lg:grid-cols-2'>
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.25 }}
                        transition={{ duration: 0.45 }}
                        className='surface p-7'>
                        <div className='flex items-center gap-3'>
                            <span className='inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[color-mix(in_oklab,var(--color-soft)_90%,var(--color-accent)_10%)] shadow-sm'>
                                <FaChartLine aria-hidden='true' className='h-5 w-5' />
                            </span>
                            <div>
                                <p className='text-lg font-semibold tracking-tight'>Performance indicators</p>
                                <p className='text-sm text-muted'>Often visible within ~3 months</p>
                            </div>
                        </div>

                        <div className='mt-6 grid gap-3 sm:grid-cols-2'>
                            {performance.map((t) => (
                                <Tile key={t.title} title={t.title} desc={t.desc} />
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.25 }}
                        transition={{ duration: 0.45, delay: 0.05 }}
                        className='surface p-7'>
                        <div className='flex items-center gap-3'>
                            <span className='inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[color-mix(in_oklab,var(--color-soft)_90%,var(--color-accent-2)_10%)] shadow-sm'>
                                <FaSitemap aria-hidden='true' className='h-5 w-5' />
                            </span>
                            <div>
                                <p className='text-lg font-semibold tracking-tight'>Strategic value</p>
                                <p className='text-sm text-muted'>For organisations</p>
                            </div>
                        </div>

                        <div className='mt-6 grid gap-3 sm:grid-cols-2'>
                            {strategic.map((t) => (
                                <Tile key={t.title} title={t.title} desc={t.desc} />
                            ))}
                        </div>
                    </motion.div>
                </div>

                <div className='mt-8 surface-strong p-7'>
                    <p className='text-sm text-muted'>
                        The result: environments where diverse minds don’t merely cope — they excel.
                    </p>
                </div>
            </div>
        </section>
    )
}
